import * as React from "react";
import { useMediaQuery } from "react-responsive";
import { IPublicProcessedGame } from "../../../types/shifts/PublicShift";
import { prepareColorForDarkMode } from "../../../util/string";

const styles: { [key: string]: React.CSSProperties } = {
  venueContainer: {
    padding: "10 px",
  },
  column: {
    padding: "10px",
    float: "left",
  },
  td: {
    border: "1px solid black",
    backgroundColor: "theme ? theme.palette.background.paper : white",
    verticalAlign: "top",
    padding: "5px",
  },
  nogameBorder: {
    borderLeft: "1px solid black",
    borderRight: "1px solid black",
    borderTop: "0",
    borderBottom: "0",
    backgroundColor: "#eeeeee",
  },
  timeCell: {
    borderLeft: "1px solid black",
    borderRight: "1px solid black",
    borderTop: "0",
    borderBottom: "0",
    backgroundColor: "#e8e8e887",
    verticalAlign: "top",
    paddingTop: "5px",
  },
  th: {
    border: "1px solid black",
    padding: "10px",
    textAlign: "center",
    fontWeight: "bolder",
    backgroundColor: "#e8e8e887",
  },
  referees: {
    fontSize: "17px",
    fontWeight: "bolder",
  },
};

interface IPublicRosterGridProps {
  courts: string[];
  slots: { [court: string]: string | null | IPublicProcessedGame }[];
}

const PublicRosterGrid: React.FunctionComponent<IPublicRosterGridProps> = ({
  slots,
  courts,
}) => {
  const width = `${Math.trunc(90 / courts.length)}%`;
  const isDarkMode = useMediaQuery({ query: "(prefers-color-scheme: dark)" });
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const getContrastColor = (hex?: string) => {
    if (!hex) return '#000';
    let c = hex.trim();
    if (c.startsWith('#')) c = c.substring(1);
    if (c.length === 3) {
      c = c.split('').map(ch => ch + ch).join('');
    }
    if (c.length !== 6) return '#000';
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    // relative luminance
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return luminance > 0.55 ? '#000' : '#fff';
  }

  // construct some table where there is a row for every five minutes (truncate all time this 5 minutes)
  // then use the .gameLength property and use rowspan={length/5} to correctly size cells
  // note, don't return any cell in a table when there is a game there,
  // we might need to construct a different data source to faciliate this 5 minute blocking

  if (!slots) {
    return (
      <div style={{ padding: "20px" }}>
        <p>There are no games here</p>
      </div>
    );
  }

  return (
    <div style={{
      overflowX: 'auto',
      margin: isMobile ? '10px' : '20px'
    }}>
      <table
        style={{
          borderCollapse: "collapse",
          borderSpacing: 0,
          border: "1px solid black",
          margin: 0,
          minWidth: isMobile ? '100%' : 'auto',
        }}
      >
        <thead>
          <tr>
            <th style={{ ...styles.th, width: "10%" }}>
              <p>Time</p>
            </th>
            {courts.map((crt, idx) => (
              <th key={idx} style={{ width, ...styles.th }}>
                <p>{crt}</p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ textAlign: "center" }}>
          {slots
            .map((slot, idx) => (
              <tr key={idx}>
                <td key="slotCell" style={styles.timeCell}>
                  <p>{slot.slot?.toString() || ""}</p>
                </td>
                {courts.map((court, idx) => {
                  if (!(court in slot)) {
                    return <td key={idx} style={styles.nogameBorder} />;
                  }

                  const gameInfo = slot[court];
                  if (!gameInfo) {
                    // means that a game is likely ongoing
                    return null;
                  }

                  // mainly to keep typescript happy, this shouldn't ever happen
                  if (typeof gameInfo === "string") return null;

                  return (
                    <td
                      key={idx}
                      style={{ ...styles.td, width }}
                      rowSpan={gameInfo.ticks}
                    >
                      <p>
                        <b>{gameInfo.competition}</b>
                      </p>
                      <p>
                        <em>{gameInfo.teams}</em>
                      </p>
                      {gameInfo.labels && gameInfo.labels.length > 0 && (
                        <div style={{ margin: '6px 0 6px 0', display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                          {gameInfo.labels.map(l => {
                            const bg = isDarkMode ? prepareColorForDarkMode(l.color) : l.color;
                            const txt = getContrastColor(bg);
                            return (
                              <span
                                key={l.name + l.color}
                                style={{
                                  fontSize: '10px',
                                  lineHeight: 1.2,
                                  padding: '3px 8px',
                                  backgroundColor: bg,
                                  color: txt,
                                  borderRadius: '999px',
                                  fontWeight: 600,
                                  letterSpacing: '0.3px',
                                  border: '1px solid rgba(0,0,0,0.25)',
                                  boxShadow: '0 0 2px rgba(0,0,0,0.25)'
                                }}
                              >
                                {l.name}
                              </span>
                            )
                          })}
                        </div>
                      )}
                      <hr />
                      {gameInfo.officials &&
                        gameInfo.officials.map((o) => (
                          <p
                            style={{
                              ...styles.referees,
                              backgroundColor: isDarkMode
                                ? prepareColorForDarkMode(o.color)
                                : o.color,
                            }}
                            key={o.name}
                          >
                            {o.name}
                          </p>
                        ))}
                    </td>
                  );
                })}
              </tr>
            ))
            .filter((a) => a)}
        </tbody>
      </table>
    </div>
  );
};

export default PublicRosterGrid;
